import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { getListVaultsQueryKey } from '@/modules/api/codegen';
import { Modal, StaticModal, useModal } from '@/modules/modals';
import { Heading, Text } from '@/modules/ui';
import type { VaultImportModalData } from '@/modules/vaults/types';

import { ImportForm } from '../ImportForm';
import { ImportLimitReachedModal } from '../ImportLimitReachedModal';
import { ImportProgressModal } from '../ImportProgressModal';
import { ImportValidationModal } from '../ImportValidationModal';
import * as felaRules from './VaultImportModal.rules';

const MODAL_WIDTH = 520;

/**
 * Mounted once by the application shell and opened through the registry from the vault's actions:
 * `@/modules/vaults` cannot import this module without closing a cycle through the secrets module.
 * The import flow's own modals are mounted only while this one is open, so they never double up with
 * the ones the Import/Export page mounts.
 */
export const VaultImportModal = () => {
    const queryClient = useQueryClient();
    const { modal, updateModal } = useModal<VaultImportModalData>(StaticModal.VAULT_IMPORT);

    const vault = modal?.data?.vault;
    const open = Boolean(modal?.visibility && vault);

    const close = () => updateModal(false);

    return (
        <>
            <Modal open={open} closable width={MODAL_WIDTH} onClose={close} dataTestId='vault-import-modal'>
                <Heading elementType='h2' color='black'>
                    <FormattedMessage id='vaults.import.title' values={{ vault: vault?.name }} />
                </Heading>
                <Text customStyle={felaRules.text}>
                    <FormattedMessage id='vaults.import.message' />
                </Text>
                {vault && (
                    <ImportForm
                        vaultId={vault.id}
                        onSuccess={async () => {
                            // The sidebar counts come from the vault list, which the import just changed.
                            await queryClient.invalidateQueries({ queryKey: getListVaultsQueryKey() });
                            close();
                        }}
                    />
                )}
            </Modal>
            {open && (
                <>
                    <ImportValidationModal />
                    <ImportProgressModal />
                    <ImportLimitReachedModal />
                </>
            )}
        </>
    );
};
